import { useMutation } from '@apollo/client';

import { InvestorDetailsGetInvestorLegalEntitiesQuery, InvestorDetailsHouseholdNotesQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Label from '@/components/basicComponents/label';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Textarea from '@/components/basicComponents/textarea';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  INVESTOR_DETAILS_GROUP_NOTES_QUERY,
  INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY,
  INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY,
  INVESTOR_DETAILS_UPDATE_LEGAL_ENTITY_NOTES_MUTATION
} from '../queries';
import { InvestorDetailsEntityTypeEnum } from '../types';

interface IForm {
  entityId: string;
  note: string;
}

interface EditNoteProps {
  isOpen: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
  currentNote: { legalEntityId: string; date: string; note: string } | null;
  legalEntityNotes: InvestorDetailsHouseholdNotesQuery['getInvestor']['legalEntityNotes'];
  legalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
  onClose: () => void;
}

const EditNote = ({ isOpen, entityType, currentNote, legalEntityNotes, legalEntities, onClose }: EditNoteProps) => {
  const params = useParams();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, reset, control } = useForm<IForm>({ defaultValues: { entityId: '', note: '' } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentEntityName, setCurrentEntityName] = useState('');
  const [entitiesSelectOptions, setEntitiesSelectOptions] = useState<ISelectValue[]>([]);

  const getRefetchQueries = () => {
    switch (entityType) {
      case InvestorDetailsEntityTypeEnum.Household:
        return [INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY];
      case InvestorDetailsEntityTypeEnum.Group:
        return [INVESTOR_DETAILS_GROUP_NOTES_QUERY];
      case InvestorDetailsEntityTypeEnum.GroupedEntity:
      case InvestorDetailsEntityTypeEnum.UngroupedEntity:
        return [INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY];
      default:
        return [];
    }
  };

  const [updateLegalEntityNotes, { loading: updateLegalEntityNotesLoading }] = useMutation(INVESTOR_DETAILS_UPDATE_LEGAL_ENTITY_NOTES_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: getRefetchQueries()
  });

  useEffect(() => {
    if (entityType === InvestorDetailsEntityTypeEnum.Household) {
      const selectOptions = legalEntities.flatMap((entity) => {
        if (entity.isGroup && entity.entities) {
          const groupedEntities = entity.entities.map((groupedEntity) => createSimpleSelectOption(groupedEntity.id, `- ${groupedEntity.entityName}`));
          return groupedEntities;
        }
        return [createSimpleSelectOption(entity.id, entity.entityName)];
      });
      setEntitiesSelectOptions(selectOptions);
      reset({ entityId: currentNote?.legalEntityId ?? '', note: currentNote?.note ?? '' });
      return;
    }

    if (entityType === InvestorDetailsEntityTypeEnum.Group) {
      const currentGroup = legalEntities.find((entity) => entity.id === params?.entityId);
      if (currentGroup) {
        const selectOptions = currentGroup.entities.map((entity) => createSimpleSelectOption(entity.id, entity.entityName));
        setEntitiesSelectOptions(selectOptions);
        reset({ entityId: currentGroup.id, note: currentNote?.note ?? '' });
      }
      return;
    }

    const currentEntityName = legalEntityNotes.find((note) => note.legalEntityId === params?.entityId)?.name;
    if (currentEntityName) {
      setCurrentEntityName(currentEntityName);
    }

    reset({ entityId: params?.entityId ?? '', note: currentNote?.note ?? '' });
  }, [currentNote, legalEntities, entityType, params?.entityId]);

  const addNewNote = async (data: IForm) => {
    try {
      setErrorMessage(null);

      const currentLegalEntityNote = legalEntityNotes.find((note) => note.legalEntityId === data.entityId);
      if (!currentLegalEntityNote) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const prevLegalEntityNotes = currentLegalEntityNote.notes.map((note) => ({
        date: note.date,
        note: note.note
      }));

      const newNotes = [{ date: new Date().toISOString(), note: data.note }, ...prevLegalEntityNotes];

      await updateLegalEntityNotes({
        variables: {
          data: {
            legalEntityId: data.entityId,
            notes: newNotes
          }
        }
      });

      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    }
  };

  const editNote = async (data: IForm) => {
    try {
      setErrorMessage(null);

      const currentLegalEntityNote = legalEntityNotes.find((note) => note.legalEntityId === data.entityId);
      if (!currentLegalEntityNote) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const prevLegalEntityNotes = currentLegalEntityNote.notes.map((note) => ({
        date: note.date,
        note: note.note
      }));

      const newNotes = prevLegalEntityNotes.map((note) => {
        if (note.note === currentNote?.note) {
          return { date: note.date, note: data.note };
        }
        return note;
      });

      await updateLegalEntityNotes({
        variables: {
          data: {
            legalEntityId: data.entityId,
            notes: newNotes
          }
        }
      });

      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    }
  };

  const onSubmit = (data: { entityId: string; note: string }) => {
    if (currentNote) {
      editNote(data);
      return;
    }
    addNewNote(data);
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={updateLegalEntityNotesLoading ? (currentNote ? 'Edit Note' : 'Add New Note') : null}
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(onSubmit),
        disabled: updateLegalEntityNotesLoading
      }}
      showRequiredFields={!updateLegalEntityNotesLoading}
      showCancelButton={!updateLegalEntityNotesLoading}
      showCloseIcon={!updateLegalEntityNotesLoading}
      isUseOutsideClick={!updateLegalEntityNotesLoading}
    >
      {updateLegalEntityNotesLoading ? (
        <ModalLoader />
      ) : (
        <>
          <ModalHeader>
            <ModalTitle>{currentNote ? 'Edit Note' : 'Add New Note'}</ModalTitle>
            {currentNote ||
            entityType === InvestorDetailsEntityTypeEnum.GroupedEntity ||
            entityType === InvestorDetailsEntityTypeEnum.UngroupedEntity ? (
              <Label size="md">
                Add a note for <b>{currentEntityName}</b>.
              </Label>
            ) : (
              <Label size="md">Select an entity to add a note.</Label>
            )}
          </ModalHeader>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalBody>
            {!currentNote && (entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group) && (
              <Controller
                name="entityId"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Select Entity is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    label="Select Entity"
                    value={value}
                    setValue={onChange}
                    options={entitiesSelectOptions}
                    errorText={error?.message}
                    size="md"
                    isRequiredField
                  />
                )}
              />
            )}
            <Controller
              name="note"
              control={control}
              rules={{
                validate: (value) => !value || value?.length <= 250 || 'Maximum number of characters exceeded'
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Textarea
                    label="Note"
                    value={value}
                    onChange={onChange}
                    errorText={error?.message}
                    limitCharactersTo={250}
                    autoFocus={!currentNote}
                    size="md"
                    isRequiredField
                  />
                </>
              )}
            />
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default EditNote;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h1`
  font-family: 'Blinker', serif;
  font-weight: 400;
  font-size: 33px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;
